import { useEffect, useState } from 'react';
import { orgPixel, ApiError, type OrgPixel } from '@/lib/api';
import { DashboardLayout } from '@/components/DashboardLayout';
import { SettingsSubNav } from '@/components/SettingsSubNav';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  PixelVerificationDialog,
  type PixelVerificationResult,
} from '@/components/PixelVerificationDialog';

/**
 * /app/settings/pixel — connect the org's Meta Pixel for server-side
 * Conversions API tracking. Pro/Agency feature.
 *
 * One pixel per org applies to all links by default. Per-link overrides
 * are configured in the link editor (the "Meta Pixel (advanced)" section
 * on the link edit form — see PageEditor / NewLink).
 *
 * Token handling mirrors the admin platform-pixel page:
 *   - Server never returns plaintext token (only `hasToken: bool`).
 *   - Empty token field on a saved pixel = leave it alone (re-save other
 *     fields like testEventCode without re-pasting the token).
 *   - To remove the pixel entirely, use Disconnect.
 *
 * Free plan: page renders the same explainer but the form is disabled +
 * shows an upgrade CTA. `orgPixel.get()` returns canEdit=false for free.
 */
export default function SettingsPixel() {
  const [data, setData] = useState<OrgPixel | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const [pixelId, setPixelId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [tokenTouched, setTokenTouched] = useState(false);
  const [testEventCode, setTestEventCode] = useState('');
  const [lduEu, setLduEu] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [verifyResult, setVerifyResult] = useState<PixelVerificationResult | null>(null);

  function load() {
    orgPixel
      .get()
      .then((r) => {
        setData(r);
        setPixelId(r.pixelId || '');
        setTestEventCode(r.testEventCode || '');
        setLduEu(Boolean(r.lduEu));
        setAccessToken('');
        setTokenTouched(false);
      })
      .catch((e) => setErr((e as Error).message || 'Failed to load pixel.'));
  }

  useEffect(() => {
    load();
  }, []);

  async function save() {
    setBusy(true);
    setErr(null);
    setSaved(false);
    setVerifyResult(null);
    try {
      const body: {
        pixelId: string;
        accessToken?: string;
        testEventCode?: string | null;
        lduEu?: boolean;
      } = {
        pixelId: pixelId.trim(),
        testEventCode: testEventCode.trim() || null,
        lduEu,
      };
      if (tokenTouched && accessToken) {
        body.accessToken = accessToken;
      }
      await orgPixel.put(body);
      setSaved(true);
      // Show the verified modal only when we actually exercised Meta —
      // i.e. when a new token was sent. Otherwise the save just touched
      // testEventCode / lduEu and didn't validate anything.
      if (tokenTouched && accessToken) {
        setVerifyResult({ ok: true });
      }
      load();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'pixel_validation_failed') {
        const raw = (e.raw || {}) as {
          metaError?: string;
          metaCode?: number;
          httpStatus?: number;
        };
        setVerifyResult({
          ok: false,
          metaError: raw.metaError,
          metaCode: raw.metaCode,
          httpStatus: raw.httpStatus,
        });
      } else {
        const msg = (e as Error).message || '';
        if (msg.includes('plan_required')) {
          setErr('Connecting a Meta Pixel is a Pro feature. Upgrade to enable it.');
        } else if (msg.includes('pixel_id_required')) {
          setErr('Pixel ID is required.');
        } else if (msg.includes('invalid_pixel_id')) {
          setErr('Pixel ID must be a numeric string (15-16 digits typically).');
        } else if (msg.includes('access_token_required_on_first_save')) {
          setErr('Paste your access token to connect for the first time.');
        } else if (msg.includes('token_cannot_be_empty')) {
          setErr('To remove the pixel, use the Disconnect button instead.');
        } else {
          setErr(msg || 'Save failed. Try again.');
        }
      }
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    setBusy(true);
    setErr(null);
    try {
      await orgPixel.disconnect();
      load();
    } catch (e) {
      setErr((e as Error).message || 'Disconnect failed.');
    } finally {
      setBusy(false);
      setDisconnectOpen(false);
    }
  }

  const canEdit = data?.canEdit ?? false;
  const connected = data?.connected ?? false;
  const hasStoredToken = data?.hasToken ?? false;
  const plan = data?.plan;

  return (
    <DashboardLayout
      title="Settings"
      titleAction={
        plan && (
          <span className="text-sm text-[var(--color-on-surface-variant)]">
            {plan} plan
          </span>
        )
      }
    >
      <SettingsSubNav />

      <div className="max-w-2xl space-y-5">
        {/* Explainer card */}
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--surface-glass)] p-4 text-sm">
          <div className="font-semibold">Meta Pixel · Conversions API</div>
          <p className="mt-1 text-[13px] text-[var(--color-muted-foreground)]">
            Sends a server-side <code>PageView</code> event to your Meta
            pixel every time someone clicks one of your links. This is the
            same pixel ID you'd use in browser-side <code>fbq()</code>, but
            fired from our server — so it works even when ad-blockers,
            privacy extensions, or iOS tracking restrictions kill the
            browser pixel. Builds retargeting audiences and attributes
            conversions Meta would otherwise miss.
          </p>
          <p className="mt-2 text-[13px] text-[var(--color-muted-foreground)]">
            <strong>Where this applies:</strong> all your links use this
            pixel by default. You can override per-link from the link
            editor if you want a specific link to use a different pixel.
          </p>
        </div>

        {/* Plan-gate CTA for Free */}
        {!canEdit && (
          <div className="rounded-xl border border-[var(--color-primary)]/40 bg-[var(--color-primary)]/10 p-4 text-sm">
            <div className="font-semibold">Upgrade to Pro to connect a pixel</div>
            <p className="mt-1 text-[13px] text-[var(--color-muted-foreground)]">
              Server-side Conversions API tracking is included in Pro and
              Agency plans. The free tier doesn't include outbound CAPI
              calls because they have per-event costs we can't subsidise at
              scale.
            </p>
          </div>
        )}

        {err && (
          <p className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
            {err}
          </p>
        )}

        {/* Form — disabled when free plan */}
        <div className="space-y-4 rounded-xl border border-[var(--color-border)] p-4">
          <Field
            label="Pixel ID"
            hint="The numeric Pixel ID from Meta Events Manager. Same ID you'd use for the browser pixel."
          >
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              value={pixelId}
              onChange={(e) => setPixelId(e.target.value)}
              placeholder="e.g. 681153731669782"
              disabled={!canEdit}
              className={inputCls}
            />
          </Field>

          <Field
            label="Access token (Conversions API)"
            hint={
              hasStoredToken && !tokenTouched
                ? 'A token is already stored. Leave empty to keep it; paste a new value to replace it.'
                : 'Generate at Meta Events Manager → your pixel → Settings → Conversions API → Generate access token.'
            }
          >
            <input
              type="password"
              value={accessToken}
              onChange={(e) => {
                setAccessToken(e.target.value);
                setTokenTouched(true);
              }}
              placeholder={
                hasStoredToken && !tokenTouched
                  ? '•••• already set'
                  : 'Paste the EAA… token here'
              }
              autoComplete="new-password"
              disabled={!canEdit}
              className={inputCls}
            />
          </Field>

          <Field
            label="Test event code (optional)"
            hint="When set, events appear in Meta's Test Events tab for debugging. Leave blank for production."
          >
            <input
              type="text"
              value={testEventCode}
              onChange={(e) => setTestEventCode(e.target.value)}
              placeholder="TEST12345"
              disabled={!canEdit}
              className={inputCls}
            />
          </Field>

          <label className="flex items-start gap-3 rounded-lg border border-[var(--color-border)] p-3">
            <input
              type="checkbox"
              checked={lduEu}
              onChange={(e) => setLduEu(e.target.checked)}
              disabled={!canEdit}
              className="mt-0.5"
            />
            <span>
              <span className="block text-sm font-medium">
                Limited Data Use for EU traffic
              </span>
              <span className="block text-[12px] text-[var(--color-muted-foreground)]">
                Sends Meta's LDU flag for visitors from EU countries so the
                event is processed under GDPR-friendly rules. Recommended
                if you have any EU audience.
              </span>
            </span>
          </label>

          <div className="flex items-center gap-3 pt-1">
            <button
              type="button"
              onClick={save}
              disabled={busy || !canEdit}
              className="rounded-md bg-[var(--color-primary)] px-4 py-2 text-sm font-medium text-[var(--color-primary-foreground)] disabled:opacity-50"
            >
              {busy
                ? tokenTouched && accessToken
                  ? 'Verifying with Meta…'
                  : 'Saving…'
                : connected
                ? 'Save changes'
                : 'Connect pixel'}
            </button>
            {connected && canEdit && (
              <button
                type="button"
                onClick={() => setDisconnectOpen(true)}
                disabled={busy}
                className="text-sm text-[var(--color-destructive)] hover:underline disabled:opacity-50"
              >
                Disconnect
              </button>
            )}
            {saved && (
              <span className="text-xs text-[var(--color-muted-foreground)]">Saved.</span>
            )}
            {data?.updatedAt && connected && (
              <span className="ml-auto text-[11px] text-[var(--color-muted-foreground)]">
                Last updated{' '}
                {new Date(data.updatedAt * 1000).toLocaleString()}
              </span>
            )}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={disconnectOpen}
        title="Disconnect Meta Pixel?"
        description="Your links will stop firing PageView events to this pixel. Per-link pixel overrides keep working. You can reconnect anytime."
        confirmLabel="Disconnect"
        destructive
        onConfirm={disconnect}
        onClose={() => setDisconnectOpen(false)}
      />

      <PixelVerificationDialog
        open={verifyResult !== null}
        result={verifyResult}
        onClose={() => setVerifyResult(null)}
      />
    </DashboardLayout>
  );
}

const inputCls =
  'mt-1 block w-full rounded-md border border-[var(--color-border)] bg-transparent px-2.5 py-1.5 text-sm outline-none transition focus:border-[var(--color-ring)] disabled:opacity-60';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-xs font-medium uppercase tracking-wider text-[var(--color-muted-foreground)]">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-[11px] text-[var(--color-muted-foreground)]">
          {hint}
        </span>
      )}
    </label>
  );
}
